/**
 * The openings the ENGINE builds — one owner for both plan-view consumers.
 *
 * `BuildInput.openings` is what the user authored, not what gets built. Two
 * adjustments happen inside the engine before a wall is framed:
 *
 *  - a two-room with `entryWall: "long"` has its front door REPLACED by a
 *    long-wall door derived from the partition split, and
 *  - panorama glazing removes the right-wall windows, because that wall becomes
 *    one glass section.
 *
 * `resolveOpeningAdjustmentOpenings` owns both rules, and `deriveInteriorDoors`
 * already validates against its output. The on-screen drawing and the PDF sheet
 * were still reading the raw list, so both drew a door the built sauna does not
 * contain, at a position no engine would ever put it, and left a window on a
 * wall the model builds solid. Same family as the clearance-overlay bug in
 * #131: two paths to one truth, one of which is the one the engine builds from.
 *
 * `resolvePlanOpenings` is therefore the ONLY way anything under `PlanView/`
 * gets an opening list, and it delegates to the engine's own resolver rather
 * than restating it.
 *
 * Note what that did to the parity test below. It was written to catch two
 * adapters drifting apart; with one owner there is nothing left to drift, so
 * that half of it can no longer fail. What it still proves is narrower and
 * worth keeping: every door `deriveInteriorDoors` validates appears in the
 * drawn list ON THE SAME WALL, which exercises the room-assignment step the
 * plan does not share with the engine. Consolidating two paths into one always
 * costs the parity test that guarded them — see the #131 note.
 */
import { resolveEngineOpenings } from "../../../engines/interiorLayoutResolver";
import type { BuildInput } from "../../../engines/types";

/**
 * The opening set the engine frames, for a build input.
 *
 * This calls the engine's own `resolveEngineOpenings` rather than restating its
 * expression. An earlier version mirrored it here, guarded by the parity test
 * below — but a parity test only protects the dimensions it compares, and that
 * one compares id and wall. A position change in the engine's resolution would
 * have matched both while the plan drew the door in the wrong place: the exact
 * failure this module exists to prevent, reintroduced one level up.
 *
 * The re-export keeps `PlanView/` importing its opening list from one place, so
 * the merge and editability rules below sit beside the resolution they depend on.
 */
export function resolvePlanOpenings(input: BuildInput): BuildInput["openings"] {
	return resolveEngineOpenings(input);
}

/**
 * Fold a plan-view edit of the RESOLVED set back onto the authored one.
 *
 * The drawing hands the whole opening list to `setOpenings`, so committing the
 * resolved list verbatim would persist the resolution: the panorama-filtered
 * windows would be deleted from the design and the authored front door would be
 * replaced by a generated one. Only openings the user actually owns are
 * updated — matched by id — and everything else is passed through untouched.
 *
 * Openings the resolution INVENTED (the long-wall entry door) have no authored
 * counterpart and are dropped here, which is the honest outcome: their position
 * is a function of the partition split, and the next regeneration would discard
 * any edit to it anyway. The view does not offer them as drag targets for the
 * same reason.
 */
export function mergePlanOpeningEdits(
	authored: BuildInput["openings"],
	edited: BuildInput["openings"],
): BuildInput["openings"] {
	const byId = new Map(edited.map((opening) => [opening.id, opening]));
	return authored.map((opening) => {
		const next = byId.get(opening.id);
		return next ? { ...opening, ...next } : opening;
	});
}

/** Ids the user owns, and may therefore drag. */
export function authoredOpeningIds(input: BuildInput): ReadonlySet<string> {
	return new Set(input.openings.map((opening) => opening.id));
}
